import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import financeIcon from '@/assets/drbfm/finance.svg';
import scheduleIcon from '@/assets/drbfm/schedule.svg';
import docsIcon from '@/assets/drbfm/docs.svg';
import compareIcon from '@/assets/drbfm/text-compare.svg';
import playIcon from '@/assets/drbfm/play.svg';
import infoIcon from '@/assets/drbfm/info.svg';
import { CADModelSelect } from '@/components/cad/CADModelSelect';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import SpecificationField from './SpecificationField';
import { useKeepFocusAboveFooter } from './useKeepFocusAboveFooter';
import { useFirstRunViewportHeight } from './useFirstRunViewportHeight';
import './firstRunInput.css';
import type { FirstRunFieldKey, FirstRunInputForm } from './useFirstRunInputForm';
import {
  ROLES_BY_MODE,
  type DocumentSourceSlotProps,
  type FirstRunInputSubmission,
  type SpecificationMode,
} from './types';

export interface DrbfmFirstRunInputFormProps {
  form: FirstRunInputForm;
  onStart: (submission: FirstRunInputSubmission) => void;
  isStarting?: boolean;
  disabled?: boolean;
  cadEnabled?: boolean | null;
  documentOnly?: boolean;
  /** The registered-document source (#1636), rendered in every field; absent means text only. */
  renderDocumentSource?: (props: DocumentSourceSlotProps) => React.ReactNode;
}

const MODES: readonly SpecificationMode[] = ['old_new', 'difference'];
const MODE_ICONS: Record<SpecificationMode, string> = { old_new: docsIcon, difference: compareIcon };

const DrbfmFirstRunInputForm: React.FC<DrbfmFirstRunInputFormProps> = ({
  form,
  onStart,
  isStarting = false,
  disabled = false,
  cadEnabled = null,
  documentOnly = false,
  renderDocumentSource,
}) => {
  const { t } = useTranslation();
  const controlsDisabled = isStarting || disabled;
  const formElement = useRef<HTMLFormElement>(null);
  useFirstRunViewportHeight(formElement);
  const controls = useRef<Partial<Record<FirstRunFieldKey, HTMLElement | null>>>({});
  const footer = useRef<HTMLDivElement>(null);
  const keepFocusAboveFooter = useKeepFocusAboveFooter(footer);
  const bind = (key: FirstRunFieldKey) => (element: HTMLElement | null) => {
    controls.current[key] = element;
  };

  const submit = (event: React.SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (controlsDisabled) return;
    const result = form.validate();
    if (result.ok) onStart(result.submission);
    else controls.current[result.firstInvalid]?.focus();
  };

  return (
    <form ref={formElement} noValidate onSubmit={submit} onFocus={keepFocusAboveFooter} className="drbfm-first-run-input flex flex-col gap-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="drbfm-first-run-heading revy-cjk flex flex-wrap items-center gap-3">
          <span className="grid size-8 shrink-0 place-items-center"><img src={financeIcon} alt="" /></span>
          <span>{t('drbfm.title')}</span>
          <span>{t('drbfm.firstRunInput.heading')}</span>
        </h2>
        <Button asChild variant="outline" className="drbfm-first-run-history">
          <Link to="/drbfm/history">
            <img src={scheduleIcon} className="size-5" alt="" />
            <span className="revy-cjk">{t('drbfm.firstRunInput.history')}</span>
          </Link>
        </Button>
      </div>

      {/* Figma 246:2551: one card, basic information on a muted band above the specifications. */}
      <Card className="drbfm-first-run-card overflow-hidden">
        <section className="drbfm-first-run-basic">
          <h3 className="revy-cjk mb-7">{t('drbfm.firstRunInput.basicInfo')}</h3>
          <div className="grid grid-cols-1 gap-6 min-[900px]:grid-cols-2">
            <Field label={t('drbfm.firstRunInput.itemName')} required error={form.errors.itemName}>
              {(control) => (
                <Input
                  {...control}
                  ref={bind('itemName')}
                  value={form.itemName}
                  onChange={(event) => form.setBasic('itemName', event.target.value)}
                  placeholder={t('drbfm.firstRunInput.itemNamePlaceholder')}
                  disabled={controlsDisabled}
                />
              )}
            </Field>
            <Field label={t('drbfm.firstRunInput.title')} error={form.errors.title}>
              {(control) => (
                <Input
                  {...control}
                  ref={bind('title')}
                  value={form.title}
                  onChange={(event) => form.setBasic('title', event.target.value)}
                  placeholder={t('drbfm.firstRunInput.titlePlaceholder')}
                  disabled={controlsDisabled}
                />
              )}
            </Field>
            {cadEnabled === true && (
              <div className="drbfm-first-run-cad min-[900px]:col-span-2">
                <CADModelSelect
                  value={form.cadModelId ?? ''}
                  onChange={form.setCadModelId}
                  disabled={controlsDisabled}
                  helperText={t('drbfm.firstRunInput.cadHelp')}
                  helperPlacement="label"
                  placeholder={t('drbfm.firstRunInput.cadPlaceholder')}
                />
              </div>
            )}
          </div>
        </section>

        <section className="drbfm-first-run-specifications">
          <Tabs
            value={form.mode}
            onValueChange={(mode) => form.setMode(mode as SpecificationMode)}
            activationMode="manual"
          >
            <div className="mb-6 flex flex-wrap items-center gap-3">
              <h3 className="revy-cjk">{t('drbfm.firstRunInput.specifications')}</h3>
              {/* Below sm the tabs stack: side by side they overflow a phone-width card. */}
              <TabsList
                aria-label={t('drbfm.firstRunInput.modeLabel')}
                className="drbfm-first-run-modes grid h-auto w-full grid-cols-1 gap-2 p-0.5 sm:inline-flex sm:w-auto sm:gap-0"
              >
                {MODES.map((mode) => {
                  const Icon = MODE_ICONS[mode];
                  return (
                    <TabsTrigger
                      key={mode}
                      value={mode}
                      aria-label={t(`drbfm.firstRunInput.modes.${mode}`)}
                      className="revy-cjk gap-1.5 whitespace-normal sm:whitespace-nowrap"
                      disabled={controlsDisabled}
                    >
                      <img src={Icon} className="size-5 shrink-0 drbfm-mode-icon" alt="" />
                      {t(`drbfm.firstRunInput.modeLabels.${mode}`)}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </div>
            {form.hiddenDraftKept && (
              <p className="revy-cjk mb-3 text-xs leading-[1.7] text-muted-foreground">
                {t('drbfm.firstRunInput.hiddenDraftKept')}
              </p>
            )}
            {MODES.map((mode) => (
              <TabsContent key={mode} value={mode} className="mt-0">
                <div
                  className={
                    mode === 'old_new' ? 'grid grid-cols-1 gap-3 min-[900px]:grid-cols-2' : 'grid grid-cols-1'
                  }
                >
                  {ROLES_BY_MODE[mode].map((role) => (
                    <SpecificationField
                      key={role}
                      role={role}
                      value={form.values[role]}
                      error={form.errors[role]}
                      disabled={controlsDisabled}
                      documentOnly={documentOnly}
                      controlRef={bind(role)}
                      sourceOptions={form.sourceOptions[role]}
                      onSourceChange={form.switchSource}
                      onEditCopy={form.copyDocumentText}
                      onTextChange={form.setText}
                      onDocumentChange={form.changeDocument}
                      renderDocumentSource={renderDocumentSource}
                    />
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>

          <Field
            className="mt-6"
            label={t('drbfm.firstRunInput.supplement')}
            help={form.errors.supplement ? undefined : t('drbfm.firstRunInput.supplementHelp')}
            error={form.errors.supplement}
          >
            {(control) => (
              <Textarea
                {...control}
                ref={bind('supplement')}
                value={form.supplement}
                onChange={(event) => form.setBasic('supplement', event.target.value)}
                placeholder={t('drbfm.firstRunInput.supplementPlaceholder')}
                disabled={controlsDisabled}
                rows={3}
                className="revy-cjk"
              />
            )}
          </Field>
        </section>
      </Card>

      <div
        ref={footer}
        // Sticky only from sm: on a phone a pinned footer would take a large share of the screen.
        className="drbfm-first-run-footer z-10 mt-auto flex flex-col gap-3 border-t px-4 py-4 sm:sticky sm:bottom-0 sm:flex-row sm:items-center sm:justify-between"
      >
        <p className="revy-cjk flex min-w-0 items-start gap-2 text-xs leading-[1.7] text-muted-foreground sm:flex-1">
          <img src={infoIcon} alt="" className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          {t('drbfm.firstRunInput.analysisInfo')}
        </p>
        <Button type="submit" disabled={controlsDisabled}>
          <img src={playIcon} className="size-5" alt="" />
          <span className="revy-cjk">
            {isStarting ? t('drbfm.firstRunInput.starting') : t('drbfm.firstRunInput.start')}
          </span>
        </Button>
      </div>
    </form>
  );
};

export default DrbfmFirstRunInputForm;
